import * as React from "react";
import { Button, Modal, notification } from "antd";
import { CopyOutlined, DownloadOutlined } from "@ant-design/icons";
import { Agent } from "../../../schema/types/agent/entity";
import { SyntaxHighlight } from "../../../components/syntaxHighlight/SyntaxHighlight";
import { CustomScrollbars } from "../../../components/Scrollbar";
import { useAppSelector } from "../../../store/hooks";
import { selectContext } from "../../../store/contextSlice";
import { AgentScriptTabs } from "../agentScriptTabs";

interface Props {
  agent: Agent;
  onDone: any;
  visible: boolean;
}

export const BootstrapConfigModal: React.FC<Props> = ({ agent, onDone, visible }) => {
  const { org } = useAppSelector(selectContext);
  const protocolVersion = agent.status?.protocolVersion || "v1";
  const bootstrapConfig = agent.status?.bootstrapConfig;
  const bootstrapConfigJson = JSON.stringify(bootstrapConfig, null, 2);

  const [tookAction, setTookAction] = React.useState(false);
  const [step, setStep] = React.useState(1);

  async function copyToClipboard(text: string, message: string = "Copied to clipboard") {
    navigator.clipboard.writeText(text);
    notification.success({ message });
    setTookAction(true);
  }

  function downloadFile(content: string, filename: string) {
    const file = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(file);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setTookAction(true);
  }

  function localOnDone() {
    const link = agent.links.find((l: any) => l.rel === "self")!.href;
    onDone(link);
  }

  // v1: Single step with bootstrap config
  if (protocolVersion === "v1") {
    return (
      <Modal
        title={"Agent Bootstrap Config"}
        open={visible}
        maskClosable={false}
        onCancel={localOnDone}
        destroyOnHidden
        width={700}
        footer={
          <div className="flex items-center justify-end gap-4">
            <Button
              type="default"
              icon={<DownloadOutlined />}
              onClick={() => downloadFile(bootstrapConfigJson, `${agent.name}-bootstrapConfig.json`)}
            >
              Download Config File
            </Button>
            <Button type="primary" onClick={localOnDone} disabled={!tookAction}>
              Done
            </Button>
          </div>
        }
      >
        <div>
          <div>Agent bootstrap config created.</div>
          <div className="mb-4">
            Copy or download the contents, you will not have access to it after you close this screen.
          </div>
          <div className="flex items-center gap-2 mb-2">
            <Button size="small" icon={<CopyOutlined />} onClick={() => copyToClipboard(bootstrapConfigJson)}>
              Copy
            </Button>
          </div>
          <SyntaxHighlight code={bootstrapConfigJson} language="json" showLineNumbers />
        </div>
      </Modal>
    );
  }

  // v2: Two-step flow
  return (
    <Modal
      title={step === 1 ? "Agent Bootstrap Config" : "Download Scripts"}
      open={visible}
      maskClosable={false}
      onCancel={localOnDone}
      destroyOnHidden
      styles={{
        body: { height: "70vh", display: "flex", flexDirection: "column" },
        content: { minWidth: 600, maxWidth: "90vw" },
      }}
      width={1200}
      footer={
        <div className="flex items-center justify-between">
          <div>{step === 2 && <Button onClick={() => setStep(1)}>Back</Button>}</div>
          <div className="flex items-center gap-4">
            {step === 1 ? (
              <>
                <Button
                  type="default"
                  icon={<DownloadOutlined />}
                  onClick={() => downloadFile(bootstrapConfigJson, `${agent.name}-bootstrapConfig.json`)}
                >
                  Download Config File
                </Button>
                <Button
                  type="primary"
                  onClick={() => {
                    setTookAction(true);
                    setStep(2);
                  }}
                >
                  Next
                </Button>
              </>
            ) : (
              <Button type="primary" onClick={localOnDone} disabled={!tookAction}>
                Done
              </Button>
            )}
          </div>
        </div>
      }
    >
      {step === 1 ? (
        <div className="flex flex-col flex-1 p-4">
          <div>Agent bootstrap config created.</div>
          <div className="mb-4">
            Copy or download the contents, you will not have access to it after you close this screen.
          </div>
          <div className="flex items-center gap-2 mb-2">
            <Button size="small" icon={<CopyOutlined />} onClick={() => copyToClipboard(bootstrapConfigJson)}>
              Copy
            </Button>
          </div>
          <CustomScrollbars className="flex-1 border rounded" style={{ minHeight: 0 }}>
            <SyntaxHighlight code={bootstrapConfigJson} language="json" showLineNumbers />
          </CustomScrollbars>
        </div>
      ) : (
        <div className="h-full p-4">
          <AgentScriptTabs
            org={org}
            agentName={agent.name}
            bootstrapConfig={bootstrapConfig!}
            onAction={() => setTookAction(true)}
          />
        </div>
      )}
    </Modal>
  );
};
